import { BULK_SETTABLE_STATUSES, isBulkSettableStatus, type BulkSettableStatus } from '@/types/inventory';

interface BulkSetStatusControlProps {
  value: BulkSettableStatus | '';
  onChange: (status: BulkSettableStatus | '') => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  /** Disables the control without the "Setting..." label, e.g. while another bulk action runs. */
  disabled?: boolean;
}

/**
 * Status picker + "Set Status" button for bulk status changes on selected inventory items.
 * Shared by the Inventory page's bulk bar and the manifest barcode list's selection bar; the
 * caller owns the selection, confirmation and API call.
 */
export function BulkSetStatusControl({ value, onChange, onSubmit, isSubmitting, disabled = false }: BulkSetStatusControlProps) {
  const isDisabled = isSubmitting || disabled;
  return (
    <div className="flex items-center gap-2">
      <select
        aria-label="New status for selected items"
        value={value}
        onChange={(e) => onChange(isBulkSettableStatus(e.target.value) ? e.target.value : '')}
        disabled={isDisabled}
        className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50"
      >
        <option value="">Status...</option>
        {BULK_SETTABLE_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!value || isDisabled}
        className="px-3 py-1.5 text-sm rounded bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 transition-colors whitespace-nowrap"
      >
        {isSubmitting ? 'Setting...' : 'Set Status'}
      </button>
    </div>
  );
}
